const VISITOR_ID_KEY = 'avatar_visitor_id';
const SESSION_ID_KEY = 'avatar_session_id';
const SESSION_ACTIVITY_KEY = 'avatar_session_last_activity';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const HEARTBEAT_MS = 30 * 1000;

export type AnalyticsInputType = 'text' | 'voice' | 'suggestion';

type EventData = Record<string, boolean | number | string | null | undefined>;

const newId = (): string => window.crypto.randomUUID();

const getAnalyticsUrl = (chatApiUrl?: string): string | null =>
  chatApiUrl ? chatApiUrl.replace(/\/chat\/?$/, '/analytics/events') : null;

const getContext = (): EventData => {
  const params = new URLSearchParams(window.location.search);
  const userAgent = navigator.userAgent;
  const deviceType = /iPad|Tablet/i.test(userAgent)
    ? 'tablet'
    : /Mobi|Android|iPhone/i.test(userAgent)
      ? 'mobile'
      : 'desktop';
  const operatingSystem = /Windows/i.test(userAgent)
    ? 'Windows'
    : /Android/i.test(userAgent)
      ? 'Android'
      : /iPhone|iPad|Mac OS/i.test(userAgent)
        ? 'Apple'
        : /Linux/i.test(userAgent)
          ? 'Linux'
          : 'Other';
  const browser = /Edg\//i.test(userAgent)
    ? 'Edge'
    : /Firefox\//i.test(userAgent)
      ? 'Firefox'
      : /Chrome\//i.test(userAgent)
        ? 'Chrome'
        : /Safari\//i.test(userAgent)
          ? 'Safari'
          : 'Other';
  let referrerHost: string | null = null;
  try {
    referrerHost = document.referrer ? new URL(document.referrer).hostname : null;
  } catch {
    referrerHost = null;
  }
  return {
    referrer_host: referrerHost,
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    device_type: deviceType,
    operating_system: operatingSystem,
    browser,
  };
};

export const getAnalyticsHeaders = (): Record<string, string> => {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = newId();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  const now = Date.now();
  const lastActivity = Number(localStorage.getItem(SESSION_ACTIVITY_KEY) || 0);
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId || now - lastActivity > SESSION_IDLE_MS) {
    sessionId = newId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  localStorage.setItem(SESSION_ACTIVITY_KEY, String(now));
  return { 'X-Avatar-Visitor-ID': visitorId, 'X-Avatar-Session-ID': sessionId };
};

export const trackAnalyticsEvent = (chatApiUrl: string | undefined, eventType: string, eventData?: EventData): void => {
  const url = getAnalyticsUrl(chatApiUrl);
  if (!url) return;
  const headers = getAnalyticsHeaders();
  const payload = JSON.stringify({
    visitor_id: headers['X-Avatar-Visitor-ID'],
    session_id: headers['X-Avatar-Session-ID'],
    event_type: eventType,
    event_data: eventData,
    ...getContext(),
  });
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
};

export const trackAnalyticsSession = (chatApiUrl?: string): (() => void) | undefined => {
  if (!chatApiUrl) return undefined;
  trackAnalyticsEvent(chatApiUrl, 'session_started');
  const heartbeat = window.setInterval(() => {
    if (document.visibilityState === 'visible') trackAnalyticsEvent(chatApiUrl, 'session_heartbeat');
  }, HEARTBEAT_MS);
  const onVisibilityChange = () => trackAnalyticsEvent(
    chatApiUrl,
    document.visibilityState === 'visible' ? 'page_visible' : 'page_hidden',
  );
  const onPageHide = () => trackAnalyticsEvent(chatApiUrl, 'session_finished');
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    window.clearInterval(heartbeat);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };
};
